import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { history } from '../../../helpers';
import Navbar from '../../Navbar';
import GroupChat from '../GroupChat';
import { gameActions, questionActions, profileActions, userActions } from '../../../actions';
import { useLoggedUser } from '../../../hooks/useLoggedUser';
import { extractQuestionMediaUrls, preloadMediaUrls } from '../../../helpers/mediaPreloader';
import RightArrow from '../../../assets/img/right-arrow.svg';
import LeftArrow from '../../../assets/img/left-arrow.svg';

const PHOTO_NO_CHOICE_COIN_PREFIX = 'pp-photo-no-choice-coin:';

const canUseSessionStorage = () => {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch {
    return false;
  }
};

const getPhotoNoChoiceCoinKey = (studentProfileId, gameId, questionId) => {
  if (!studentProfileId || !gameId || !questionId) {
    return null;
  }

  return `${PHOTO_NO_CHOICE_COIN_PREFIX}${studentProfileId}:${gameId}:${questionId}`;
};

const hasCapturedPhotoNoChoiceCoin = (studentProfileId, gameId, questionId) => {
  const storageKey = getPhotoNoChoiceCoinKey(studentProfileId, gameId, questionId);
  if (!storageKey || !canUseSessionStorage()) {
    return false;
  }

  try {
    return window.sessionStorage.getItem(storageKey) === 'Y';
  } catch {
    return false;
  }
};

const markPhotoNoChoiceCoinCaptured = (studentProfileId, gameId, questionId) => {
  const storageKey = getPhotoNoChoiceCoinKey(studentProfileId, gameId, questionId);
  if (!storageKey || !canUseSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, 'Y');
  } catch {
    // ignore storage errors
  }
};

const GameQuestion = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { gameId, gameName } = location?.state ?? {}; // Access GameID (optional chaining for direct nav / missing state)
  const [question, setQuestion] = useState({});
  const [flagCoin, setFlagCoin] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showCoin, setShowCoin] = useState(false);
  const [coinSpeed, setCoinSpeed] = useState(null);
  const [answerStatus, setAnswerStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(null);
  const [nextDirection, setNextDirection] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [randomPosition, setRandomPosition] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  const [xmark, setXMark] = useState('');
  const [checkmark, setCheckMark] = useState('');

  const loggedUser = useLoggedUser();
  const { appconfigs } = useSelector(state => state.config);
  const { blockedstatus } = useSelector((state) => state.games);
  const isGameBlocked = blockedstatus?.BlockGame === true;

  const coinTimeoutConfig = appconfigs?.find(config => config.ConfigName === 'COINFLAG_TIMEOUT');
  const coinTimeout = coinTimeoutConfig?.ConfigValue || 10000;


  const emptyRetryRef = useRef(false);
  const requestRef = useRef(0);
  // Ref to hold the timeout ID
  const timeoutIdRef = useRef(null);
  const submitLockRef = useRef(false);
  const resumeLockRef = useRef(false);

  useEffect(() => {
    if (!gameId || !loggedUser?.data?.StudentProfileID) {
      history.replace('/');
      return;
    }

    dispatch(gameActions.fetchBlockedStatus(loggedUser.data.StudentProfileID, true));

    getGameQuestion(
      loggedUser.data.StudentProfileID,
      gameId,
      null,
      null,
      'next'
    );
  }, []);

  // Stop in-progress play immediately when admin blocks game (socket/Redux).
  useEffect(() => {
    if (!isGameBlocked) {
      submitLockRef.current = false;
      return;
    }

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    submitLockRef.current = true;
    setIsSubmitting(false);
    setShowCoin(false);
    setAnswerStatus(null);
    setSubmittingAnswer(null);
    setSelectedAnswer('');
  }, [isGameBlocked]);

  useEffect(() => {
    dispatch(questionActions.fetchCoinImage())
      .then((response) => {
        if (response) {
          setFlagCoin(response);
        }
        if (response && response.CoinSpeed) {
          switch (response.CoinSpeed) {
            case 'veryslow':
              setCoinSpeed(30);
              break;
            case 'slow':
              setCoinSpeed(25);
              break;
            case 'medium':
              setCoinSpeed(20);
              break;
            case 'fast':
              setCoinSpeed(15);
              break;
            case 'veryfast':
              setCoinSpeed(10);
              break;
            default:
              setCoinSpeed(20); // default speed if the value is unrecognized
          }
        }
      })
      .catch(error => {
        console.log('Error::', error);
      });
  }, [dispatch]);

  useEffect(() => {
    clearExistingTimeout();
  }, [question?.QuestionID]);

  const coinStyle = {
    animation: `drift ${coinSpeed}s linear infinite`
  };

  useEffect(() => {
    dispatch(userActions.fetchLandingMedia())
      .then(response => {
        if (!Array.isArray(response)) return;
        response.forEach(media => {
          if (media.Category === "checkmark") {
            setCheckMark(media.MediaURL);
          } else if (media.Category === "xmark") {
            setXMark(media.MediaURL);
          }
        });
      })
      .catch(error => {
        console.log('Error:: ', error);
      });
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        resumeLockRef.current = true;
      }

      if (document.visibilityState === 'visible') {
        resumeLockRef.current = false;
        submitLockRef.current = false;

        clearExistingTimeout();

        setIsSubmitting(false);
        setShowCoin(false);
        setAnswerStatus(null);
        setSubmittingAnswer(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const clearExistingTimeout = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearExistingTimeout();
    };
  }, []);

  useEffect(() => {
    preloadMediaUrls([flagCoin?.MediaURL, checkmark, xmark], { fetchpriority: 'high' });
  }, [flagCoin?.MediaURL, checkmark, xmark]);

  useEffect(() => {
    preloadMediaUrls(extractQuestionMediaUrls(question), { fetchpriority: 'high' });
  }, [question]);

  const fetchMypoints = () => {
    if (loggedUser?.data?.StudentProfileID) {
      const isGameSubmit = true;
      dispatch(profileActions.fetchMypoints(loggedUser.data.StudentProfileID, isGameSubmit));
    }
  };

  const isPhotoNoChoiceCoinAvailable = (currentQuestion = question) => {
    if (currentQuestion?.QuestionType !== 'Photo No Choice') {
      return false;
    }

    if (currentQuestion?.is_Answered !== 'N' || currentQuestion?.ShowCoin === 'N') {
      return false;
    }

    return !hasCapturedPhotoNoChoiceCoin(
      loggedUser?.data?.StudentProfileID,
      currentQuestion?.GameID ?? gameId,
      currentQuestion?.QuestionID
    );
  };

  const navigateToHomePage = () => {
    const currentGameId = question?.GameID ?? gameId;
    const studentProfileId = loggedUser?.data?.StudentProfileID;
    const gameMenu = loggedUser?.data?.GameMenu;

    if (currentGameId != null) {
      dispatch(gameActions.markGameAsPlayed(currentGameId));
    }

    if (studentProfileId && gameMenu != null) {
      dispatch(gameActions.fetchGames(studentProfileId, gameMenu, true));
    }

    history.push({ pathname: "/" });
  };

  const getGameQuestion = (studentid, gameid, questionid, gamelineid, direction) => {
    if (isGameBlocked) return;

    const currentReq = ++requestRef.current;
    dispatch(questionActions.fetchQuestion(studentid, gameid, questionid, gamelineid, direction))
      .then((response) => {
        // Handle empty API wake-up response after idle
        if (!response || Object.keys(response).length === 0) {
          submitLockRef.current = false;
          setIsSubmitting(false);

          if (!emptyRetryRef.current) {
            emptyRetryRef.current = true;

            getGameQuestion(
              loggedUser.data.StudentProfileID,
              gameid,
              questionid,
              gamelineid,
              direction
            );
          }
          return;
        }
        // Reset retry flag on success
        emptyRetryRef.current = false;
        if (currentReq !== requestRef.current) {
          return;
        }

        setAnswerStatus(null);
        setSelectedAnswer('');
        setSubmittingAnswer(null);
        setIsAnswered(false);
        setIsSubmitting(false);
        setShowCoin(false);

        setQuestion(response);
      })
      .catch(error => {
        setIsSubmitting(false);
        console.log('Error::', error);
      });
  };

  const renderMediaPreview = (url) => {
    if (url == null || url === '') return null;
    const urlStr = typeof url === 'string' ? url : String(url);
    const mediaType = urlStr.split('.').pop().toLowerCase();

    const key = urlStr; // Use URL as key to force re-render

    if (['bmp', 'gif', 'jpg', 'jpeg', 'png', 'webp', 'svg'].includes(mediaType)) {
      return (
        <img
          key={`${urlStr}_${question.QuestionMediaID}`}
          src={urlStr}
          decoding="async"
          loading="eager"
          fetchpriority="high"
          onLoad={() => {
            if (isPhotoNoChoiceCoinAvailable(question)) {
              generateRandomPosition();
              setShowCoin(true);
            }
          }}
          onError={() => undefined}
          alt="Media"
        />
      );
    } else if (['mp4', 'webm'].includes(mediaType)) {
      return (
        <video key={key} preload="auto" width="80%" controls style={{ marginTop: '10px' }}>
          <source src={urlStr} type={`video/${mediaType}`} />
          Your browser does not support the video tag.
        </video>
      );
    } else if (['mp3', 'wav'].includes(mediaType)) {
      return (
        <audio key={key} preload="auto" controls style={{ marginTop: '10px', width: '300px', height: '130px' }}>
          <source src={urlStr} type={`audio/${mediaType}`} />
          Your browser does not support the audio tag.
        </audio>
      );
    }
    return null;
  };

  const handleSelectOption = (answer) => {
    if (isGameBlocked) return;

    if (selectedAnswer === answer) {
      setSelectedAnswer('');
    } else {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmitAnswer = (direction) => {
    if (isGameBlocked) return;

    // recover from iPad idle lock
    if (document.visibilityState === "visible" && submitLockRef.current) {
      // If we resume from idle, unlock once so NEXT works
      submitLockRef.current = false;
    }

    if (
      submitLockRef.current ||
      resumeLockRef.current ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);
    setNextDirection(direction);
    setShowCoin(false);

    // Clear any existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null; // Reset ref
    }

    let answerToSubmit = selectedAnswer;

    // Check conditions for default answer, but only if the question has not been answered yet
    const isDefaultConditionMet =
      (question.QuestionType === 'Video/audio Single Choice' || question.QuestionType === 'Photo No Choice') &&
      !question.Option_1 && !question.Option_2 && !question.Option_3 && !question.Option_4 &&
      question.is_Answered === 'N' && !isAnswered;

    if (isDefaultConditionMet) {
      answerToSubmit = 'default';
      setSelectedAnswer('default');
      setIsAnswered(true); // Mark as answered
    }

    if (answerToSubmit && answerToSubmit !== '') {
      dispatch(questionActions.submitAnswer({
        GameID: question.GameID,
        StudentID: loggedUser.data.StudentProfileID,
        GameLineID: question.GameLineID,
        SelectedAnswer: answerToSubmit
      }))
        .then((response) => {
          if (response && response.ok === true) {
            if (question.QuestionType !== 'Photo No Choice') {
              setSubmittingAnswer(answerToSubmit);
              if (answerToSubmit === question.CorrectAnswer) {
                setAnswerStatus('correct');
              } else {
                setAnswerStatus('incorrect');
              }
              setTimeout(() => {
                if (question.ShowCoin === "Y" || (question.ShowCoin === "CA" && question.CorrectAnswer === answerToSubmit)) {
                  setShowCoin(true);
                  setSelectedAnswer('');
                  clearExistingTimeout();
                  timeoutIdRef.current = setTimeout(() => {
                    submitLockRef.current = false;
                    setIsSubmitting(false);
                    setShowCoin(false);
                    setAnswerStatus(null);
                    setSubmittingAnswer(null);

                    getGameQuestion(
                      loggedUser.data.StudentProfileID,
                      question.GameID,
                      question.QuestionID,
                      question.GameLineID,
                      direction
                    );
                  }, coinTimeout);
                } else {
                  submitLockRef.current = false;
                  setIsSubmitting(false);
                  setAnswerStatus(null);
                  setSubmittingAnswer(null);
                  getGameQuestion(
                    loggedUser.data.StudentProfileID,
                    question.GameID,
                    question.QuestionID,
                    question.GameLineID,
                    direction
                  );
                  setSelectedAnswer('');
                }

                // Trigger `fetchMypoints` after answer submission
                fetchMypoints();
              }, 1000);
            } else {
              submitLockRef.current = false;
              setIsSubmitting(false);
              setAnswerStatus(null);
              setSubmittingAnswer(null);
              getGameQuestion(
                loggedUser.data.StudentProfileID,
                question.GameID,
                question.QuestionID,
                question.GameLineID,
                direction
              );
              // Trigger `fetchMypoints` after answer submission
              fetchMypoints();
            }
          }
        })
        .catch(error => {
          setIsSubmitting(false);
          console.log('Error::', error);
        });
    } else {
      // If no answer selected, immediately fetch the next question
      submitLockRef.current = false;
      setIsSubmitting(false);
      setShowCoin(false);
      setAnswerStatus(null);
      setSubmittingAnswer(null);
      getGameQuestion(loggedUser.data.StudentProfileID, question.GameID, question.QuestionID, question.GameLineID, direction);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const handleFinishGame = () => {
    if (isGameBlocked || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    setShowCoin(false);
    setNextDirection(null);
    setIsSubmitting(true);

    // Clear any existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null; // Reset ref
    }

    let answerToSubmit = selectedAnswer;
    setSelectedAnswer('');

    // Check conditions for default answer, but only if the question has not been answered yet
    const isDefaultConditionMet =
      (question.QuestionType === 'Video/audio Single Choice' || question.QuestionType === 'Photo No Choice') &&
      !question.Option_1 && !question.Option_2 && !question.Option_3 && !question.Option_4 &&
      question.is_Answered === 'N' && !isAnswered;

    if (isDefaultConditionMet) {
      answerToSubmit = 'default';
      setSelectedAnswer('default');
      setIsAnswered(true); // Mark as answered
    }

    if (answerToSubmit && answerToSubmit !== '') {
      dispatch(questionActions.submitAnswer({
        GameID: question.GameID,
        StudentID: loggedUser.data.StudentProfileID,
        GameLineID: question.GameLineID,
        SelectedAnswer: answerToSubmit
      }))
        .then((response) => {
          setSubmittingAnswer(answerToSubmit);
          if (response && response.ok === true) {
            if (question.QuestionType !== 'Photo No Choice') {
              if (answerToSubmit === question.CorrectAnswer) {
                setAnswerStatus('correct');
              } else {
                setAnswerStatus('incorrect');
              }
              setTimeout(() => {
                if (question.ShowCoin === "Y" || (question.ShowCoin === "CA" && question.CorrectAnswer === answerToSubmit)) {
                  setShowCoin(true);
                  clearExistingTimeout();
                  timeoutIdRef.current = setTimeout(() => {
                    setShowCoin(false);
                    setSelectedAnswer('');
                    navigateToHomePage();
                  }, coinTimeout);
                } else {
                  setSelectedAnswer('');
                  navigateToHomePage();
                }
                // Trigger `fetchMypoints` after answer submission
                fetchMypoints();
              }, 1000);
            } else {
              setShowCoin(false);
              navigateToHomePage();
              // Trigger `fetchMypoints` after answer submission
              fetchMypoints();
            }
          }

        })
        .catch(error => {
          setIsSubmitting(false);
          console.log('Error::', error);
        });
    } else {
      navigateToHomePage();
    }
  };

  const handleCaptureCoin = () => {
    if (isGameBlocked) return;

    setShowCoin(false);
    dispatch(questionActions.captureCoin({
      StudentID: loggedUser.data.StudentProfileID,
      QuestionID: question.QuestionID,
      CoinImageCount: flagCoin.CoinCount
    }))
      .then((response) => {
        if (response && response.ok === true) {
          if (question.QuestionType === 'Photo No Choice') {
            markPhotoNoChoiceCoinCaptured(
              loggedUser?.data?.StudentProfileID,
              question?.GameID ?? gameId,
              question?.QuestionID
            );
            setQuestion(prevQuestion => ({
              ...prevQuestion,
              ShowCoin: 'N',
            }));
          }

          // showToast('You won the Award!!', 'success');
          if (question.QuestionType !== 'Photo No Choice') {
            if (nextDirection !== null) {
              submitLockRef.current = false;
              setIsSubmitting(false);
              setSelectedAnswer('');
              setAnswerStatus(null);
              setSubmittingAnswer(null);
              getGameQuestion(loggedUser.data.StudentProfileID, question.GameID, question.QuestionID, question.GameLineID, nextDirection);
            } else {
              setSelectedAnswer('');
              navigateToHomePage();
            }
          }
          // Trigger `fetchMypoints` after answer submission
          fetchMypoints();
        }
      })
      .catch(error => {
        console.log('Error::', error);
      });
  };

  const generateRandomPosition = () => {
    const top = 25 + Math.random() * (57 - 25);
    const bottom = 30 + Math.random() * (70 - 30);
    const left = 20 + Math.random() * (50 - 20);
    const right = 20 + Math.random() * (50 - 20);
    setRandomPosition({ top, bottom, left, right });
  };

  return (
    <React.Fragment>
      <div className="row ">
        <div className="col-lg-9 col-sm-9 col-md-9 ques-page p-0">
          <Navbar />
          <div className="container ques-cont">
            <h2 className="b-font game-name-h main-heading-top">
              {gameName ? gameName : ''}
            </h2>

            {isGameBlocked ? (
              <div className="blocked-msg">
                You have been blocked from the game. Please contact your admin to regain access.
              </div>
            ) : (
            <>
            {showCoin && question.QuestionType !== 'Photo No Choice' && (
              <div className='animation'>
                {flagCoin.MediaURL && (
                  <div className="cloud fcloud01" style={coinStyle}>
                    <img src={flagCoin.MediaURL} className='w-75' onClick={handleCaptureCoin} alt="Coin" decoding="async" loading="eager" fetchpriority="high" />
                  </div>
                )}
              </div>
            )}

            {showCoin && isPhotoNoChoiceCoinAvailable(question) && (
              <div className='animation'>
                {flagCoin.MediaURL && (
                  <div
                    className='cloud'
                    style={{
                      position: 'absolute',
                      top: `${randomPosition.top}%`,
                      bottom: `${randomPosition.bottom}%`,
                      left: `${randomPosition.left}%`,
                      right: `${randomPosition.right}%`
                    }}
                  >
                    <img src={flagCoin.MediaURL} className='w-75' onClick={handleCaptureCoin} alt="Coin" decoding="async" loading="eager" fetchpriority="high" />
                  </div>
                )}
              </div>
            )}

            {question.Question_layout && question.Question_layout === "right" && question.QuestionType === "Image Single Choice" ? (

              <div className="w-90 img-single-layout quest-div">
                {/* this is for the Image single layout condition starts hear ------------- */}
                {question.GameQuestion && (
                  <div className="g-ques d-flex align-items-center justify-content-center text-center">
                    <label>
                      {question.GameQuestion}
                    </label>
                  </div>
                )}

                <div className="answer-div mt-2">
                  <div className="row d-flex flex-row m-auto justify-content-between w-100">
                    {(question.QuestionType === "Image Single Choice") && (
                      <div className="col-sm-6 col-lg-6 col-md-6">
                        <div className="d-flex flex-column answer-col w-60">
                          <div className="ans-blue-bx m-">
                            <div className='question-img-div'>
                              {renderMediaPreview(question.QuestionMediaID)}
                            </div>
                          </div>
                          {answerStatus === 'correct' && (
                            <div className='answer-stat'>
                              <img src={checkmark} alt="Correct Answer" />
                            </div>
                          )}
                          {answerStatus === 'incorrect' && (
                            <div className='answer-stat'>
                              <img src={xmark} alt="Wrong Answer" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="col-sm-6 col-lg-6 col-md-6">

                      <div className="d-flex flex-column justify-content-center ans-div">
                        <div className='d-flex flex-column justify-content-between h-100'>
                          {question.Option_1 && (
                            <div className="card ">
                              <button
                                className={`h-100 
                          ${selectedAnswer === question.Option_1 || question.SelectedAnswer === question.Option_1 || submittingAnswer === question.Option_1 ? 'active' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer === question.Option_1 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer !== question.Option_1 ? 'wrong-ans-btn' : ''}
                          ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                                onClick={() => handleSelectOption(question.Option_1)}
                                disabled={question.is_Answered === 'Y' || isSubmitting}
                              >
                                {question.Option_1}
                                {answerStatus && question.Option_1 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.Option_1 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer !== question.Option_1 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                              </button>
                            </div>
                          )}
                          {question.Option_2 && (
                            <div className="card ">
                              <button
                                className={`h-100 ${selectedAnswer === question.Option_2 || question.SelectedAnswer === question.Option_2 || submittingAnswer === question.Option_2 ? 'active' : ''} 
                        ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer === question.Option_2 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer !== question.Option_2 ? 'wrong-ans-btn' : ''}
                        ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                                onClick={() => handleSelectOption(question.Option_2)}
                                disabled={question.is_Answered === 'Y' || isSubmitting}
                              >
                                {question.Option_2}
                                {answerStatus && question.Option_2 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.Option_2 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer !== question.Option_2 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                              </button>
                            </div>
                          )}
                          {question.Option_3 && (
                            <div className="card ">
                              <button
                                className={`h-100 ${selectedAnswer === question.Option_3 || question.SelectedAnswer === question.Option_3 || submittingAnswer === question.Option_3 ? 'active' : ''} 
                        ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer === question.Option_3 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer !== question.Option_3 ? 'wrong-ans-btn' : ''}
                        ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                                onClick={() => handleSelectOption(question.Option_3)}
                                disabled={question.is_Answered === 'Y' || isSubmitting}
                              >
                                {question.Option_3}
                                {answerStatus && question.Option_3 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.Option_3 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer !== question.Option_3 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                              </button>
                            </div>
                          )}
                          {question.Option_4 && (
                            <div className="card">
                              <button
                                className={`h-100 ${selectedAnswer === question.Option_4 || question.SelectedAnswer === question.Option_4 || submittingAnswer === question.Option_4 ? 'active' : ''} 
                        ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer === question.Option_4 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer !== question.Option_4 ? 'wrong-ans-btn' : ''}
                        ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                                onClick={() => handleSelectOption(question.Option_4)}
                                disabled={question.is_Answered === 'Y' || isSubmitting}
                              >
                                {question.Option_4}
                                {answerStatus && question.Option_4 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.Option_4 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                                {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer !== question.Option_4 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* this is for the Image single layout condition ends hear ------------- */}
              </div>

            ) : (

              <div className="w-90 quest-div">
                {question.GameQuestion && (
                  <div className="g-ques d-flex align-items-center justify-content-center text-center">
                    <label>
                      {question.GameQuestion}
                    </label>
                  </div>
                )}

                <div className="answer-div mt-2">
                  <div className="row d-flex flex-row justify-content-between">
                    {(question.QuestionType === "Image Single Choice" || question.QuestionType === "Video/audio Single Choice" || question.QuestionType === 'Photo No Choice') && (
                      <div className="col-sm-12 col-lg-12 col-md-12">
                        <div className="d-flex flex-column answer-col">
                          <div className="ans-blue-bx">
                            {renderMediaPreview(question.QuestionMediaID)}
                          </div>
                          {answerStatus === 'correct' && (
                            <div className='answer-stat'>
                              <img src={checkmark} alt="Correct Answer" />
                            </div>
                          )}
                          {answerStatus === 'incorrect' && (
                            <div className='answer-stat'>
                              <img src={xmark} alt="Wrong Answer" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {question.QuestionType === "Text Only Multiple Choice" && (
                      <div className="col-sm-12 col-lg-12 col-md-12">
                        <div className="d-flex flex-column answer-col">
                          <div className="ans-blue-bx">
                          </div>
                          {answerStatus === 'correct' && (
                            <div className='answer-stat'>
                              <img src={checkmark} alt="Correct Answer" />
                            </div>
                          )}
                          {answerStatus === 'incorrect' && (
                            <div className='answer-stat'>
                              <img src={xmark} alt="Wrong Answer" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {question.QuestionType === "Image Multiple Choice" && (
                      <div className='multiple-img-div'>
                        {question.MediaID_1 && (
                          <div className="img-multiple">
                            <div className="d-flex flex-column answer-col">
                              <div className="ans-blue-bx">
                                <img src={question.MediaID_1} alt="Option 1" decoding="async" loading="eager" fetchpriority="high" />
                              </div>
                              {answerStatus === 'correct' && (
                                <div className='answer-stat'>
                                  <img src={checkmark} alt="Correct Answer" />
                                </div>
                              )}
                              {answerStatus === 'incorrect' && (
                                <div className='answer-stat'>
                                  <img src={xmark} alt="Wrong Answer" />
                                </div>
                              )}

                            </div>
                          </div>
                        )}
                        {question.MediaID_2 && (
                          <div className=" img-multiple">
                            <div className="d-flex flex-column answer-col">
                              <div className="ans-blue-bx">
                                <img src={question.MediaID_2} alt="Option 2" decoding="async" loading="eager" fetchpriority="high" />
                              </div>

                            </div>
                          </div>
                        )}
                        {question.MediaID_3 && (
                          <div className="  img-multiple">
                            <div className="d-flex flex-column answer-col">
                              <div className="ans-blue-bx">
                                <img src={question.MediaID_3} alt="Option 3" decoding="async" loading="eager" fetchpriority="high" />
                              </div>

                            </div>
                          </div>
                        )}
                        {question.MediaID_4 && (
                          <div className=" img-multiple">
                            <div className="d-flex flex-column answer-col">
                              <div className="ans-blue-bx">
                                <img src={question.MediaID_4} alt="Option 4" decoding="async" loading="eager" fetchpriority="high" />
                              </div>

                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-row justify-content-between ans-div">
                    {question.Option_1 && (
                      <div className="card ">
                        <button
                          className={`h-100 
                          ${selectedAnswer === question.Option_1 || question.SelectedAnswer === question.Option_1 || submittingAnswer === question.Option_1 ? 'active' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer === question.Option_1 ? 'right-ans-btn' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer !== question.Option_1 ? 'wrong-ans-btn' : ''} 
                          ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                          onClick={() => handleSelectOption(question.Option_1)}
                          disabled={question.is_Answered === 'Y' || isSubmitting}
                        >
                          {question.Option_1}
                          {answerStatus && question.Option_1 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.Option_1 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_1 && question.CorrectAnswer !== question.Option_1 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                        </button>
                      </div>
                    )}

                    {question.Option_2 && (
                      <div className="card ">
                        <button
                          className={`h-100 ${selectedAnswer === question.Option_2 || question.SelectedAnswer === question.Option_2 || submittingAnswer === question.Option_2 ? 'active' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer === question.Option_2 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer !== question.Option_2 ? 'wrong-ans-btn' : ''}
                          ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                          onClick={() => handleSelectOption(question.Option_2)}
                          disabled={question.is_Answered === 'Y' || isSubmitting}
                        >
                          {question.Option_2}
                          {answerStatus && question.Option_2 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.Option_2 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_2 && question.CorrectAnswer !== question.Option_2 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                        </button>
                      </div>
                    )}
                    {question.Option_3 && (
                      <div className="card ">
                        <button
                          className={`h-100 ${selectedAnswer === question.Option_3 || question.SelectedAnswer === question.Option_3 || submittingAnswer === question.Option_3 ? 'active' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer === question.Option_3 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer !== question.Option_3 ? 'wrong-ans-btn' : ''}
                          ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                          onClick={() => handleSelectOption(question.Option_3)}
                          disabled={question.is_Answered === 'Y' || isSubmitting}
                        >
                          {question.Option_3}
                          {answerStatus && question.Option_3 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.Option_3 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_3 && question.CorrectAnswer !== question.Option_3 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                        </button>
                      </div>
                    )}
                    {question.Option_4 && (
                      <div className="card">
                        <button
                          className={`h-100 ${selectedAnswer === question.Option_4 || question.SelectedAnswer === question.Option_4 || submittingAnswer === question.Option_4 ? 'active' : ''} 
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer === question.Option_4 ? 'right-ans-btn' : ''}
                          ${question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer !== question.Option_4 ? 'wrong-ans-btn' : ''}
                          ${question.is_Answered === 'Y' ? 'disabled' : ''}`}
                          onClick={() => handleSelectOption(question.Option_4)}
                          disabled={question.is_Answered === 'Y' || isSubmitting}
                        >
                          {question.Option_4}
                          {answerStatus && question.Option_4 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.Option_4 === question.CorrectAnswer && <>&nbsp;<i className="fa fa-check" aria-hidden="true"></i></>}
                          {question.is_Answered === 'Y' && question.SelectedAnswer === question.Option_4 && question.CorrectAnswer !== question.Option_4 && <>&nbsp;<i className="fa fa-times" aria-hidden="true"></i></>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Arrow buttons */}
            <div className="btm-arrows">
              <div className="d-flex w-100 justify-content-between btm-arrow-div">
                {question.isPrevAvailable ? (
                  <button className="arrow-btns nav-arrow" disabled={
                    isSubmitting ||
                    resumeLockRef.current ||
                    document.visibilityState !== 'visible'
                  } onClick={() => handleSubmitAnswer('previous')} aria-label="Go to previous question">
                    <img src={LeftArrow} alt="Previous" className="w-100" />
                  </button>
                ) : (
                  <button className="arrow-btns" disabled aria-label="No previous question available">
                  </button>
                )}

                {question.isNextAvailable === true && (
                  <button
                    className="arrow-btns nav-arrow right-btn"
                    disabled={
                      isSubmitting ||
                      resumeLockRef.current ||
                      document.visibilityState !== 'visible'
                    }
                    onClick={() => handleSubmitAnswer('next')}
                  >
                    <img src={RightArrow} alt="Next" className="w-100" />
                  </button>
                )}

                {question.isNextAvailable === false && (
                  <div className="ans-div col-lg-3 col-sm-3 col-md-3">
                    <button className="btn finish-btn" onClick={() => handleFinishGame()}>
                      Finish
                    </button>
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        </div>

        <div className="col-lg-3 col-sm-3 col-md-3 chat-area p-0">
          <GroupChat />
        </div>

      </div>
    </React.Fragment>
  );
};

export default GameQuestion;
